import React, { useEffect, useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';

// Official WA DOR Form 27-0036 — SAME source the wholesale portal uses
// (frontend/src/pages/Portal/TaxExemption.tsx). Keep these in sync.
const WA_FORM_URL = 'https://dor.wa.gov/sites/default/files/2022-02/27-0036.pdf';

// API host for resolving relative certificate URLs. Computed inline (same rule
// as services/api.ts) so this component doesn't pull the axios import chain
// into jsdom test bundles.
const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api')
  .replace(/\/+$/, '').replace('/api', '');

interface ActiveCert {
  id: number;
  certificate_number: string | null;
  issuing_state: string;
  expiration_date: string;
  days_until_expiration: number;
}
interface TaxRequest {
  id: number;
  status: string;
  status_display?: string;
  exemption_type?: string;
  exemption_type_display?: string;
  certificate_number?: string;
  issuing_state?: string;
  expiration_date?: string;
  certificate_url?: string | null;
  staff_notes?: string;
  created_at?: string;
}

const EXEMPTION_TYPES = [
  { value: 'AG_PRODUCTION', label: 'Agricultural Production' },
  { value: 'AG_RESALE', label: 'Agricultural Resale' },
  { value: 'NONPROFIT', label: 'Nonprofit Organization' },
  { value: 'GOVERNMENT', label: 'Government Entity' },
  { value: 'OTHER', label: 'Other' },
];

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

const openCert = (url?: string | null) => {
  if (!url) return;
  window.open(url.startsWith('http') ? url : `${API_HOST}${url}`, '_blank', 'noopener');
};

/**
 * Tax Exemption section for the BC member profile — at parity with the
 * wholesale portal (pages/Portal/TaxExemption.tsx): current-cert card, WA
 * form download, upload panel, certificate history, who-qualifies.
 *
 * Local members ONLY (the parent gates on member.local_access). Wires the
 * EXISTING member endpoints; never applies exemption itself.
 *
 * The WA-form download and upload panels are ALWAYS rendered for local
 * members — they are NOT hidden on a failed fetch or a non-can_submit state,
 * which is what previously collapsed this section to text only.
 */
const TaxExemptionSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{
    is_tax_exempt: boolean;
    active_certificate: ActiveCert | null;
    pending_requests: TaxRequest[];
    can_submit: boolean;
  } | null>(null);
  const [history, setHistory] = useState<TaxRequest[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState({
    exemption_type: 'AG_PRODUCTION',
    certificate_number: '',
    issuing_state: 'WA',
    expiration_date: '',
    member_notes: '',
  });
  const [file, setFile] = useState<File | null>(null);

  const load = async () => {
    setLoading(true);
    // Fetch independently — a history failure must NOT blank the whole
    // section (the old coupling is what produced the text-only bug).
    try {
      setStatus(await bridgeClubAPI.getTaxExemptionStatus());
    } catch {
      setStatus(null);
    }
    try {
      const h = await bridgeClubAPI.getTaxExemptionHistory();
      setHistory(h.requests || []);
    } catch {
      setHistory([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!file) { setError('Please attach your certificate file.'); return; }
    if (!form.expiration_date) { setError('Please enter the certificate expiration date.'); return; }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('certificate_file', file);
      fd.append('exemption_type', form.exemption_type);
      fd.append('certificate_number', form.certificate_number);
      fd.append('issuing_state', form.issuing_state);
      fd.append('expiration_date', form.expiration_date);
      fd.append('member_notes', form.member_notes);
      await bridgeClubAPI.submitTaxExemption(fd);
      setSuccess('Your tax exemption request has been submitted for review.');
      setShowForm(false);
      setFile(null);
      await load();
    } catch (err: any) {
      setError(err.error || err.message || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="stats-card">
        <h3>Tax Exemption</h3>
        <p style={{ color: '#666' }}>Loading…</p>
      </div>
    );
  }

  const active = status?.active_certificate || null;
  const expired = !!active && active.days_until_expiration < 0;
  const expiringSoon = !!active && !expired && active.days_until_expiration <= 30;
  const pending = status?.pending_requests || [];
  const canSubmit = !!status?.can_submit;
  // Active cert's downloadable file lives on its originating approved request.
  const approvedDownloadUrl = history.find(r => r.status === 'APPROVED')?.certificate_url || null;

  const badge = (bg: string, color: string, label: string) => (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 700, background: bg, color, marginLeft: 8,
    }}>{label}</span>
  );

  return (
    <div className="stats-card">
      <h3>Tax Exemption</h3>

      {success && <p style={{ color: '#2D5A27', fontWeight: 600 }}>{success}</p>}
      {error && <p style={{ color: '#B91C1C', fontWeight: 600 }}>{error}</p>}

      {/* ── Current Tax Exemption Certificate ── */}
      {active && (
        <div style={{
          border: `1px solid ${expired ? '#EF4444' : expiringSoon ? '#F59E0B' : '#10B981'}`,
          borderRadius: 10, padding: 14, marginBottom: 14,
          background: expired ? '#FEF2F2' : expiringSoon ? '#FFFBEB' : '#ECFDF5',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontWeight: 700 }}>
              {active.certificate_number || `Certificate #${active.id}`}
              {expired
                ? badge('#FEE2E2', '#B91C1C', 'Expired')
                : expiringSoon
                  ? badge('#FEF3C7', '#B45309', 'Expiring Soon')
                  : badge('#D1FAE5', '#047857', 'Active')}
            </div>
            {approvedDownloadUrl && (
              <button onClick={() => openCert(approvedDownloadUrl)}
                style={{ background: '#2D5A27', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}>
                Download Certificate
              </button>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 8, display: 'grid', gap: 3 }}>
            <div><strong>Issuing State:</strong> {active.issuing_state}</div>
            <div><strong>Expiration Date:</strong> {fmtDate(active.expiration_date)}</div>
            <div>
              <strong>{expired ? 'Days Since Expiration:' : 'Days Until Expiration:'}</strong>{' '}
              <span style={{ color: expired ? '#B91C1C' : expiringSoon ? '#B45309' : 'inherit', fontWeight: 600 }}>
                {expired ? `${Math.abs(active.days_until_expiration)} days ago` : `${active.days_until_expiration} days`}
              </span>
            </div>
          </div>
          {expired && (
            <p style={{ marginTop: 8, color: '#B91C1C', fontSize: 13 }}>
              <strong>Certificate expired.</strong> Upload a current form below to restore your exemption.
            </p>
          )}
          {expiringSoon && (
            <p style={{ marginTop: 8, color: '#B45309', fontSize: 13 }}>
              <strong>Expiring soon.</strong> Upload a renewal below to stay tax-exempt.
            </p>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 14,
          background: '#EFF6FF', border: '1px solid #3B82F6', color: '#1D4ED8',
        }}>
          <strong>⏳ Request pending staff review</strong>
          <div style={{ fontSize: 13, marginTop: 4, color: '#374151' }}>
            We'll notify you once it's processed.
          </div>
        </div>
      )}

      {/* ── Download Official WA Form ── */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 12 }}>
        <h4 style={{ margin: '0 0 6px' }}>Download Official WA Form</h4>
        <p style={{ fontSize: 13, color: '#444', margin: '0 0 10px' }}>
          Download the WA State Farmer's Certificate (Form 27-0036), fill it out and sign it, then upload it below.
        </p>
        <a href={WA_FORM_URL} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-block', background: '#2D5A27', color: '#fff', textDecoration: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 600 }}>
          Download Form (PDF)
        </a>
      </div>

      {/* ── Upload Your Certificate ── */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 12 }}>
        <h4 style={{ margin: '0 0 6px' }}>Upload Your Certificate</h4>
        {!canSubmit && !showForm ? (
          <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
            {pending.length > 0
              ? 'Your submitted certificate is awaiting review.'
              : active
                ? 'You have an active certificate on file. Upload a renewal when it nears expiry.'
                : 'A certificate is already on file.'}
          </p>
        ) : !showForm ? (
          <>
            <p style={{ fontSize: 13, color: '#444', margin: '0 0 10px' }}>
              Once you've filled out the form, upload it here for our records.
            </p>
            <button className="edit-btn" onClick={() => setShowForm(true)}>
              {active ? 'Upload Renewed Certificate' : 'Upload Certificate'}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Certificate file (PDF or image)
              <input type="file" accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ display: 'block', marginTop: 4 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Exemption type
              <select value={form.exemption_type}
                onChange={(e) => setForm({ ...form, exemption_type: e.target.value })}
                style={{ display: 'block', marginTop: 4, width: '100%', padding: 6 }}>
                {EXEMPTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Certificate number (optional)
              <input type="text" value={form.certificate_number}
                onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
                style={{ display: 'block', marginTop: 4, width: '100%', padding: 6 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Issuing state
              <input type="text" maxLength={2} value={form.issuing_state}
                onChange={(e) => setForm({ ...form, issuing_state: e.target.value.toUpperCase() })}
                style={{ display: 'block', marginTop: 4, width: 80, padding: 6 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Expiration date
              <input type="date" value={form.expiration_date}
                onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                style={{ display: 'block', marginTop: 4, padding: 6 }} />
            </label>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Notes (optional)
              <textarea value={form.member_notes}
                onChange={(e) => setForm({ ...form, member_notes: e.target.value })}
                style={{ display: 'block', marginTop: 4, width: '100%', padding: 6 }} rows={2} />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="edit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit for Review'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} disabled={submitting}
                style={{ background: '#E5E7EB', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Certificate History ── */}
      {history.length > 0 && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 8px' }}>Certificate History</h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {history.map((r) => (
              <div key={r.id} style={{ borderBottom: '1px solid #F3F4F6', paddingBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>
                    {r.certificate_number || `Certificate #${r.id}`}
                    <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 8 }}>
                      {r.status_display || r.status}
                    </span>
                  </span>
                  {r.certificate_url && (
                    <button onClick={() => openCert(r.certificate_url)}
                      style={{ background: 'none', border: 'none', color: '#2D5A27', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                      Download
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {r.issuing_state ? `State: ${r.issuing_state} · ` : ''}
                  {r.expiration_date ? `Expires: ${fmtDate(r.expiration_date)} · ` : ''}
                  {r.created_at ? `Submitted: ${fmtDate(r.created_at)}` : ''}
                </div>
                {r.status === 'REJECTED' && r.staff_notes && (
                  <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 2 }}>
                    Not approved: {r.staff_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Who Qualifies ── */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14 }}>
        <h4 style={{ margin: '0 0 6px' }}>Who Qualifies</h4>
        <p style={{ fontSize: 13, color: '#444', margin: '0 0 8px' }}>
          The Washington State Department of Revenue defines a farmer as:
        </p>
        <blockquote style={{ fontSize: 13, color: '#374151', fontStyle: 'italic', margin: '0 0 8px', paddingLeft: 12, borderLeft: '3px solid #D1D5DB' }}>
          "A 'farmer' is any person engaged in the business of growing, raising, or producing, upon the
          person's own lands or upon the lands in which the person has a present right of possession, any
          agricultural product to be sold. It also includes person growing, raising, or producing honey bee
          products for sales, or providing bee pollination services, by an eligible apiarist. A 'farmer' does
          not include a person growing, raising, or producing such products for the person's own consumption;
          a person selling any animal or substance obtained, therefore in connection with the person's business
          of operating a stockyard or a slaughter or packaging house; or a person in respect to the business of
          taking, cultivating, or raising timber."
        </blockquote>
        <p style={{ fontSize: 13, color: '#444', margin: 0 }}>
          Feed, seed, fertilizer, and similar farm inputs purchased by a qualifying farmer are wholesale
          purchases and are not subject to sales tax (WA DOR Form 27-0036, Section A).
        </p>
      </div>
    </div>
  );
};

export default TaxExemptionSection;
